/* cSpell:ignore login setToken loadUserData setUser */
import React, { useState, useContext, useEffect } from 'react';
import { FaEye, FaEyeSlash, FaSpinner, FaSignInAlt, FaGoogle } from 'react-icons/fa';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { login } from '../api/apiService';
import { StoreContext } from '../context/StoreContext';
import '../styles/Login.css';

function Login() {
    const location = useLocation();
    const navigate = useNavigate();
    const { setToken, loadUserData, setUser } = useContext(StoreContext);

    const [formData, setFormData] = useState({
        usernameOrMobile: '',
        password: '',
        countryCode: '+91',
    });
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);

    // Check for error passed from Google callback
    useEffect(() => {
        const state = location.state;
        if (state?.error) {
            setApiError(state.error);
            window.history.replaceState({}, document.title);
        }
    }, [location]);

    const countryCodes = [
        { code: '+91', label: 'IN' },
        // { code: '+1', label: 'US' },
        // { code: '+1', label: 'CA' },
    ];

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
        setErrors((prev) => ({ ...prev, [id]: '' }));
        setApiError('');
    };

    const validate = () => {
        const tempErrors = {};
        if (!formData.usernameOrMobile.trim()) {
            tempErrors.usernameOrMobile = 'Username or Mobile Number is required.';
        }
        if (!formData.password) {
            tempErrors.password = 'Password is required.';
        }
        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setLoading(true);
        setApiError('');

        const input = formData.usernameOrMobile.trim();
        const isNumeric = /^\d+$/.test(input.replace('+', ''));
        let finalIdentifier = input;

        if (isNumeric) {
            const selectedCode = formData.countryCode.startsWith('+') ? formData.countryCode : `+${formData.countryCode}`;
            const cleanDigitsOnly = input.replace(/\D/g, '');
            if (input.startsWith(selectedCode.replace('+', ''))) {
                finalIdentifier = `+${cleanDigitsOnly}`;
            } else {
                finalIdentifier = `${selectedCode}${cleanDigitsOnly}`;
            }
        }

        const payload = {
            usernameOrMobile: finalIdentifier,
            password: formData.password
        };

        try {
            const response = await login(payload);
            if (response.data.success && response.data.token) {
                const { token: receivedToken, user: receivedUser } = response.data;
                localStorage.setItem('customer_token', receivedToken);

                let normalizedUser = null;
                if (receivedUser) {
                    // Normalize user object with all fields, including mobile and address
                    normalizedUser = {
                        id: receivedUser.id,
                        firstName: receivedUser.firstName || '',
                        middleName: receivedUser.middleName || '',
                        lastName: receivedUser.lastName || '',
                        email: receivedUser.email,
                        mobile: receivedUser.mobile || '',
                        avatar: receivedUser.avatar || '',
                        role: receivedUser.role || 'customer',
                        address: receivedUser.address || {
                            street: '',
                            city: '',
                            state: '',
                            postalCode: '',
                            country: ''
                        },
                        // Keep original name for backward compatibility
                        name: receivedUser.name || `${receivedUser.firstName || ''} ${receivedUser.lastName || ''}`.trim()
                    };
                    localStorage.setItem('user', JSON.stringify(normalizedUser));
                    setUser(normalizedUser);
                }
                setToken(receivedToken);

                try {
                    await loadUserData(receivedToken);
                } catch (profileError) {
                    setApiError('Login successful, but profile load failed.');
                    return;
                }

                // ✅ Redirect to profile completion if mobile number is missing
                if (normalizedUser && !normalizedUser.mobile) {
                    const returnTo = encodeURIComponent('/');
                    navigate(`/?returnTo=${returnTo}`);
                    return
                }

                const redirectPath = localStorage.getItem('postLoginRedirect');
                if (redirectPath) {
                    localStorage.removeItem('postLoginRedirect');
                    navigate(redirectPath);
                } else {
                    navigate('/');
                }
            } else {
                setApiError(response.data.error || 'Login failed.');
            }
        } catch (err) {
            setApiError(err.response?.data?.error || 'Invalid credentials.');
        } finally {
            setLoading(false);
        }
    };

    // Redirect to backend Google OAuth endpoint
    const handleGoogleLogin = () => {
        if (googleLoading) return;
        setGoogleLoading(true);
        localStorage.setItem('google_login_attempt', 'true');
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://vinirji.com';
        const googleUrl = `${backendUrl}/api/auth/google`;

        if (window.Android && typeof window.Android.openGoogleLogin === "function") {
            window.Android.openGoogleLogin(googleUrl);
        } else {
            window.location.href = googleUrl;
        }
        // navigate('/'); // Optional: navigate to home immediately, or you can stay on login page until redirect happens
    };

    return (
        <div className="login-page-wrapper" style={pageWrapperStyle}>
            <div className="partner" style={cardStyle}>
                <header className="partner__header" style={{ marginBottom: '1rem' }}>
                    <div style={iconContainerStyle}>
                        <FaSignInAlt size={20} color="#007bff" />
                    </div>
                    <h1 style={headingStyle}>Welcome Back!</h1>
                    {/* <p style={subtitleStyle}>Access your customer dashboard</p> */}
                </header>

                <main className="partner__main">
                    <form onSubmit={handleSubmit} noValidate>
                        {apiError && <div className="partner__error" style={errorStyle}>{apiError}</div>}

                        <div className="partner__form-group" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
                            <label style={labelStyle}>Mobile Number</label>
                            <div className="mobile-input-wrapper" style={inputRowStyle}>
                                <select
                                    id="countryCode"
                                    className="country-code-select"
                                    value={formData.countryCode}
                                    onChange={handleChange}
                                    style={countrySelectStyle}
                                >
                                    {countryCodes.map((item, index) => (
                                        <option key={index} value={item.code}>{item.label} ({item.code})</option>
                                    ))}
                                </select>
                                <input
                                    type="text"
                                    id="usernameOrMobile"
                                    className={`mobile-number-input ${errors.usernameOrMobile ? 'is-invalid' : ''}`}
                                    placeholder="Enter your mobile numbe"
                                    value={formData.usernameOrMobile}
                                    onChange={handleChange}
                                    style={mainInputStyle}
                                />
                            </div>
                            {errors.usernameOrMobile && <small style={errorTextStyle}>{errors.usernameOrMobile}</small>}
                        </div>

                        <div className="partner__form-group" style={{ textAlign: 'left', marginBottom: '1.5rem' }}>
                            <label style={labelStyle}>Password</label>
                            <div className="password-input-wrapper" style={passwordWrapperStyle}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    className={errors.password ? 'is-invalid' : ''}
                                    placeholder="Enter your password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    style={passwordInputStyle}
                                />
                                <span
                                    className="password-toggle-icon"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={eyeIconStyle}
                                >
                                    {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                </span>
                            </div>
                            {errors.password && <small style={errorTextStyle}>{errors.password}</small>}
                        </div>

                        <button type="submit" className="partner__btn" disabled={loading} style={primaryButtonStyle}>
                            {loading ? <FaSpinner className="fa-spin" /> : 'Login'}
                        </button>
                    </form>

                    {/* Continue with Google */}
                    <div style={socialButtonsContainer}>
                        <div style={dividerStyle}>
                            <span style={dividerLine}></span>
                            <span style={dividerText}><b>OR</b></span>
                            <span style={dividerLine}></span>
                        </div>
                        <button
                            onClick={handleGoogleLogin}
                            disabled={googleLoading}
                            className="google-login-btn"
                            navigate="/"
                        >
                            {googleLoading ? (
                                <FaSpinner className="fa-spin" size={16} />
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 48 48"
                                >
                                    <path
                                        fill="#FFC107"
                                        d="M43.611,20.083H42V20H24v8h11.303c-1.649,4.657-6.08,8-11.303,8c-6.627,0-12-5.373-12-12c0-6.627,5.373-12,12-12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C12.955,4,4,12.955,4,24c0,11.045,8.955,20,20,20c11.045,0,20-8.955,20-20C44,22.659,43.862,21.35,43.611,20.083z"
                                    />
                                    <path
                                        fill="#FF3D00"
                                        d="M6.306,14.691l6.571,4.819C14.655,15.108,18.961,12,24,12c3.059,0,5.842,1.154,7.961,3.039l5.657-5.657C34.046,6.053,29.268,4,24,4C16.318,4,9.656,8.337,6.306,14.691z"
                                    />
                                    <path
                                        fill="#4CAF50"
                                        d="M24,44c5.166,0,9.86-1.977,13.409-5.192l-6.19-5.238C29.211,35.091,26.715,36,24,36c-5.202,0-9.619-3.317-11.283-7.946l-6.522,5.025C9.505,39.556,16.227,44,24,44z"
                                    />
                                    <path
                                        fill="#1976D2"
                                        d="M43.611,20.083H42V20H24v8h11.303c-0.792,2.237-2.231,4.166-4.087,5.571c0.001-0.001,0.002-0.001,0.003-0.002l6.19,5.238C36.971,39.205,44,34,44,24C44,22.659,43.862,21.35,43.611,20.083z"
                                    />
                                </svg>
                            ) }
                            <span>{googleLoading ? 'Redirecting...' : 'Continue with Google'}</span>
                        </button>

                    </div>

                    <div className="bp-options" style={{ marginTop: '1rem' }}>
                        <p style={{ marginBottom: '0.5rem' }}>
                            <Link to="/forgot-password" style={linkButtonStyle}>Forgot or Reset Password?</Link>
                        </p>
                        <p style={{ fontSize: '0.95rem', color: '#333' }}>
                            Don't have an account? <Link to="/signup" style={signupLinkStyle}>Register Here</Link>
                        </p>
                    </div>
                </main>
            </div>
        </div>
    );
}

// Styles (unchanged – same as original)
// const pageWrapperStyle = {
//     backgroundColor: '#fcfcfc',
//     minHeight: '100vh',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center',
// };

const pageWrapperStyle = {
//   height: 'calc(100vh - 70px)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  paddingTop: '40px',
//   overflow: 'hidden'
};

const cardStyle = {
    width: '95%',
    maxWidth: '420px',
    // padding: '1.8rem 2rem',
    borderRadius: '2rem',
    background: '#ffffff',
    border: '1px solid #f0f0f0',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.04)',
    textAlign: 'center',
    margin: '0 auto'
};

const iconContainerStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#eef4ff',
    borderRadius: '50%',
    width: '42px',
    height: '42px',
    marginBottom: '0.5rem',
};

const headingStyle = {
    fontSize: '1.6rem',
    fontWeight: '600',
    color: '#333',
    marginBottom: '0.3rem',
    letterSpacing: '-0.5px'
};

const subtitleStyle = {
    color: '#666',
    fontSize: '1rem',
    fontWeight: '400'
};

const labelStyle = {
    fontWeight: '600',
    color: '#333',
    marginBottom: '6px',
    fontSize: '1.05rem',
    display: 'block'
};

const inputRowStyle = {
    display: 'flex',
    gap: '12px'
};

const commonInputStyle = {
    backgroundColor: '#eef4ff',
    border: '1px solid #dce6f5',
    borderRadius: '12px',
    padding: '10px 12px',
    fontSize: '0.95rem',
    color: '#333',
    outline: 'none'
};

const countrySelectStyle = {
    ...commonInputStyle,
    width: '110px'
};

const mainInputStyle = {
    ...commonInputStyle,
    flex: 1
};

const passwordWrapperStyle = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
};

const passwordInputStyle = {
    ...commonInputStyle,
    width: '100%',
    paddingRight: '45px'
};

const eyeIconStyle = {
    position: 'absolute',
    right: '15px',
    color: '#888',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center'
};

const primaryButtonStyle = {
    width: '100%',
    padding: '12px',
    fontWeight: '700',
    borderRadius: '12px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    fontSize: '1rem',
    marginTop: '0.5rem',
    cursor: 'pointer',
    boxShadow: '0 4px 15px rgba(0, 123, 255, 0.2)'
};

const linkButtonStyle = {
    color: '#007bff',
    textDecoration: 'none',
    fontSize: '0.95rem',
    fontWeight: '500'
};

const signupLinkStyle = {
    ...linkButtonStyle,
    fontWeight: '600'
};

const errorTextStyle = {
    color: '#d32f2f',
    fontSize: '0.8rem',
    marginTop: '6px',
    display: 'block'
};

const errorStyle = {
    backgroundColor: '#fff1f0',
    border: '1px solid #ffa39e',
    color: '#cf1322',
    padding: '8px',
    borderRadius: '8px',
    marginBottom: '1rem',
    fontSize: '0.85rem'
};

const socialButtonsContainer = {
    marginTop: '0.8rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
};

const dividerStyle = {
    display: 'flex',
    alignItems: 'center',
    margin: '0.5rem 0',
    gap: '10px'
};

const dividerLine = {
    flex: 1,
    height: '1px',
    backgroundColor: '#e0e0e0'
};

const dividerText = {
    color: '#888',
    fontSize: '0.85rem'
};

const socialButtonStyle = {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid #ddd',
    backgroundColor: '#fff',
    fontSize: '1rem',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    transition: 'background-color 0.2s ease'
};

const socialIconStyle = {
    fontSize: '1.2rem'
};

export default Login;

